/*
 * Copyright (c) 2020, University of Oxford
 */
import React, { useCallback, useState } from 'react'
import jwtDecode from 'jwt-decode'

import { LtiPageSettings, LtiTokenRetriever } from '@oxctl/ui-lti'
import { Heading, View, Tabs } from '@instructure/ui'

import JiscSurveysPanel from './JiscSurveysPanel'
import SisImportPanel from './SisImportPanel'

/**
 * Top-level application component.
 *
 * Responsibilities:
 * - Establish LTI context (jwt + server) using LtiTokenRetriever
 * - Render the two export panels inside Instructure Tabs once context is ready
 * - Keep tab selection controlled to preserve focus and a11y
 *
 * There are no props; LTI context is discovered at runtime via @oxctl/ui-lti.
 * @returns {JSX.Element}
 */
function App() {
  const [jwt, setJwt] = useState(null)
  const [server, setServer] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasSisPermission, setHasSisPermission] = useState(false);

  // Memoize the token handler so child effects that depend on its identity
  // (e.g., inside LtiTokenRetriever) don't re-run on every parent re-render.
  /**
   * Handle new/updated LTI token and server URL from the retriever.
   * Memoized to avoid unnecessary child updates.
   * The SIS permission is derived from the token, not passed as a parameter.
   * @param {string} token - The JWT for API calls
   * @param {string} serverArg - Base URL of the LTI tool server
   */
  const updateToken = useCallback((token, serverArg) => {
    setJwt(token)
    setServer(serverArg)

    // check the user has manage_sis permission
    const decodedJwt = jwtDecode(token)
    const jwtClaim = decodedJwt["https://purl.imsglobal.org/spec/lti/claim/custom"]
    setHasSisPermission(
      jwtClaim.canvas_membership_permissions === "manage_sis"
    );
  }, [])

  /**
   * Controlled tab change handler.
   * @param {Event} event
   * @param {{ index: number }} param1
   */
  const handleTabChange = (event, { index }) => {
    setSelectedIndex(index);
  };

  return (
    <LtiTokenRetriever handleJwt={updateToken}>
      <LtiPageSettings>
        {jwt && server ? (
          <View as="div" padding="medium">
            <Heading as='h1' level='h2' color="primary">Export Course Members</Heading>
            <Tabs margin="large auto" padding="medium" onRequestTabChange={handleTabChange}>
              <Tabs.Panel id="jisc" renderTitle="JISC Surveys" padding="large"
                isSelected={selectedIndex === 0}>
                <JiscSurveysPanel ltiServer={server} jwt={jwt} />
              </Tabs.Panel>
              {hasSisPermission && (
                <Tabs.Panel id="canvas" renderTitle="Canvas" isSelected={selectedIndex === 1} padding="large">
                  <SisImportPanel ltiServer={server} jwt={jwt} />
                </Tabs.Panel>
              )}
            </Tabs>
          </View>
        ) : (
          <div style={{ padding: '1rem' }}>Loading LTI context…</div>
        )}
      </LtiPageSettings>
    </LtiTokenRetriever>
  )
}

export default App
